import { Check } from 'lucide-react';
import * as React from 'react';
import Select, { components, SingleValue, GroupBase } from 'react-select';
import { SORTED_COUNTRY_CODES } from '@/constants/countryCodes';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';

interface CountryCodeOption {
  value: string;
  label: string;
  flag: string;
  country: string;
  code: string;
}

export interface CountrySelectionInput {
  code: string;
  country: string;
  flag: string;
}

interface Country {
  name?: string;
  code: string;
  shortName?: string;
  flag?: string;
}

export const resolveCountrySelection = (
  selected: CountrySelectionInput | null
): { country: Country; isKnownCountry: boolean } => {
  if (!selected) {
    return {
      country: { code: '', shortName: '', name: '' },
      isKnownCountry: true,
    };
  }

  const countryData = SORTED_COUNTRY_CODES.find(
    (country) => country.code === selected.code && country.country === selected.country
  );
  const source = countryData ?? selected;

  return {
    country: {
      code: source.code,
      shortName: source.country.substring(0, 3).toUpperCase(),
      name: source.country,
      flag: source.flag,
    },
    isKnownCountry: Boolean(countryData),
  };
};

interface CountryCodeSelectorProps {
  value?: Country;
  onValueChange?: (value: Country) => void;
  disabled?: boolean;
  className?: string;
  menuPortalTarget?: HTMLElement | null;
}

// Custom Option component for country codes (No changes here)
const CustomOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center space-x-3">
          <span className="text-lg">{props.data.flag}</span>
          <span className={props.isSelected ? 'font-medium text-white' : 'font-medium'}>
            {props.data.code}
          </span>
          <span className={props.isSelected ? 'text-white' : 'text-gray-600'}>
            {props.data.country}
          </span>
        </div>
        {props.isSelected && <Check className="h-4 w-4 text-white" />}
      </div>
    </components.Option>
  );
};

// MODIFIED: Custom SingleValue component for display
const CustomSingleValue = (props: any) => (
  <components.SingleValue {...props}>
    <div className="flex items-center space-x-2 overflow-hidden">
      <span className="text-lg">{props.data.flag}</span>
      <span className="font-medium">{props.data.code}</span>
      {/* The country name span has been removed from here */}
    </div>
  </components.SingleValue>
);

export function CountryCodeSelector({
  value,
  onValueChange,
  disabled = false,
  className,
  menuPortalTarget,
}: CountryCodeSelectorProps) {
  const options = React.useMemo(() => {
    return SORTED_COUNTRY_CODES.map((country) => ({
      value: `${country.code}|${country.country}`, // Make each option unique
      label: `${country.flag} ${country.code} ${country.country}`,
      flag: country.flag,
      country: country.country,
      code: country.code, // Store the original code separately
    }));
  }, []);

  const selectedOption = React.useMemo(() => {
    if (!value) return null;

    // If we have the country name, find the exact match
    if (value.name) {
      return options.find((option) => option.country === value.name);
    }

    // Fallback to first country with matching code
    return options.find((option) => option.code === value.code);
  }, [value, options]);

  const handleChange = (selected: SingleValue<CountryCodeOption | null>) => {
    const { country, isKnownCountry } = resolveCountrySelection(selected);
    if (selected && !isKnownCountry) {
      logger.warn('Selected country code is not in the country list', {
        code: selected.code,
        country: selected.country,
      });
    }
    onValueChange?.(country);
  };

  const customStyles = {
    control: (provided: any, state: { isFocused: boolean }) => ({
      ...provided,
      minHeight: '20px',
      borderColor: state.isFocused ? '#115E59' : '#E5E7EB',
      borderWidth: '1px',
      boxShadow: state.isFocused ? '0 0 0 1px #115E59' : 'none',
      '&:hover': {
        borderColor: '#115E59',
      },
      backgroundColor: '#FFFFFF',
      color: '#1F2937',
      borderRadius: '24px',
      fontSize: '14px',
      width: '120px', // Adjusted width for the shorter content
    }),
    input: (provided: any) => ({
      ...provided,
      color: '#1F2937',
      fontSize: '14px',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#1F2937',
      fontSize: '14px',
    }),
    option: (provided: any, state: { isSelected: boolean; isFocused: boolean }) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#115E59' // Teal-800 background for selected items
        : state.isFocused
          ? '#F3F4F6' // Light grey for hover/focus
          : '#FFFFFF',
      color: state.isSelected
        ? '#FFFFFF' // White text for selected items
        : '#1F2937',
      '&:active': {
        backgroundColor: '#115E59',
        color: '#FFFFFF',
      },
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      fontSize: '14px',
      borderBottom: '1px solid #F3F4F6',
      '&:last-child': {
        borderBottom: 'none',
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: '#FFFFFF',
      border: '1px solid #E5E7EB',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      zIndex: 9999,
      borderRadius: '8px',
      marginTop: '4px',
      overflow: 'hidden',
      fontSize: '14px',
      width: '300px',
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: '240px',
      overflowY: 'auto',
      padding: '8px 0',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#9CA3AF',
      fontSize: '14px',
    }),
    noOptionsMessage: (provided: any) => ({
      ...provided,
      color: '#6B7280',
      fontSize: '14px',
      padding: '12px 16px',
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      pointerEvents: 'auto',
    }),
  };

  return (
    <Select<CountryCodeOption, false, GroupBase<CountryCodeOption>>
      options={options}
      value={selectedOption}
      onChange={handleChange}
      className={cn('react-select-container', className)}
      classNamePrefix="react-select"
      isDisabled={disabled}
      menuPortalTarget={menuPortalTarget ?? undefined}
      menuPosition={menuPortalTarget ? 'fixed' : 'absolute'}
      placeholder="Country" // Changed placeholder to be more generic
      components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
      styles={customStyles}
      isSearchable
      noOptionsMessage={() => 'No countries found'}
      filterOption={(option, inputValue) => {
        const searchTerm = inputValue.toLowerCase();
        return (
          option.data.country.toLowerCase().includes(searchTerm) ||
          option.data.code.toLowerCase().includes(searchTerm)
        );
      }}
    />
  );
}
